import React, { useEffect, useState } from "react";
import { CalendarDays, Gavel, ListChecks, Ear, Award, Loader2, SlidersHorizontal } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const TYPES = [
  { key: "merit", label: "Merit cases", desc: "New merit/demerit cases and their outcomes", Icon: Award, color: "text-emerald-600 bg-emerald-100" },
  { key: "event", label: "Events", desc: "New and updated schedule events", Icon: CalendarDays, color: "text-sky-600 bg-sky-100" },
  { key: "conference", label: "Conferences", desc: "Conference scheduling and outcomes", Icon: Gavel, color: "text-rose-600 bg-rose-100" },
  { key: "choice", label: "Choices", desc: "Prompts, polls and reminders", Icon: ListChecks, color: "text-violet-600 bg-violet-100" },
  { key: "announcement", label: "Rally Call", desc: "Team-wide messages from your coach or admin", Icon: Ear, color: "text-amber-600 bg-amber-100" },
];

// Parents only care about their student's merit + conference alerts.
const ROLE_KEYS = {
  parent: ["merit", "conference"],
};

export default function NotificationPrefs() {
  const { user } = useAuth();
  const [prefs, setPrefs] = useState(null);
  const [saving, setSaving] = useState("");

  const allowed = ROLE_KEYS[user?.role];
  const types = allowed ? TYPES.filter((t) => allowed.includes(t.key)) : TYPES.filter((t) => t.key !== "merit");

  useEffect(() => {
    api.get("/notifications/preferences").then(({ data }) => setPrefs(data.preferences)).catch(() => {});
  }, []);

  const toggle = async (key, value) => {
    const prev = prefs;
    setPrefs((p) => ({ ...p, [key]: value }));
    setSaving(key);
    try {
      const { data } = await api.put("/notifications/preferences", { [key]: value });
      setPrefs(data.preferences);
    } catch {
      setPrefs(prev);
      toast.error("Could not update preference");
    } finally {
      setSaving("");
    }
  };

  if (!prefs) return null;

  return (
    <Card className="mb-5 border-slate-200 p-4 shadow-sm" data-testid="notification-prefs">
      <div className="mb-1 flex items-center gap-2">
        <SlidersHorizontal className="h-4 w-4 text-slate-500" />
        <p className="font-semibold text-slate-800">Alert preferences</p>
      </div>
      <p className="mb-4 text-sm text-slate-500">
        {user?.role === "parent"
          ? "Choose which email alerts about your student you'd like to receive. You'll always see every update in your in-app inbox below."
          : "Choose which alerts get pushed to your devices and email. You'll always see every update in your in-app inbox below."}
      </p>
      <div className="divide-y divide-slate-100">
        {types.map(({ key, label, desc, Icon, color }) => (
          <div key={key} className="flex items-center gap-3 py-3" data-testid={`notif-pref-row-${key}`}>
            <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${color}`}>
              <Icon className="h-4 w-4" />
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-slate-800">{label}</p>
              <p className="text-xs text-slate-500">{desc}</p>
            </div>
            {saving === key && <Loader2 className="h-4 w-4 animate-spin text-slate-400" />}
            <Switch
              checked={prefs[key] !== false}
              onCheckedChange={(v) => toggle(key, v)}
              data-testid={`notif-pref-switch-${key}`}
            />
          </div>
        ))}
      </div>
    </Card>
  );
}
